import React from 'react'
import { Tabs } from '@/shared/ui'
import {
  listFinanceCostCategories,
  listFinanceExpenses,
  listFinanceFixedCosts,
  type FinanceCostCategory,
  type FinanceExpense,
  type FinanceFixedCost,
} from '../services/financeCostsService'
import { FinanceCostCategoriesPanel } from './costs/FinanceCostCategoriesPanel'
import { FinanceExpensesPanel } from './costs/FinanceExpensesPanel'
import { FinanceFixedCostsPanel } from './costs/FinanceFixedCostsPanel'

type Props = {
  monthStartDate: string
  refreshKey: number
}

export function FinanceCostsTab({ monthStartDate, refreshKey }: Props) {
  const [tab, setTab] = React.useState<'recorrentes' | 'avulsos' | 'categorias'>('recorrentes')
  const [categories, setCategories] = React.useState<FinanceCostCategory[]>([])
  const [fixedCosts, setFixedCosts] = React.useState<FinanceFixedCost[]>([])
  const [expenses, setExpenses] = React.useState<FinanceExpense[]>([])
  const [loadingCats, setLoadingCats] = React.useState(true)
  const [loadingFixed, setLoadingFixed] = React.useState(true)
  const [loadingExp, setLoadingExp] = React.useState(true)
  const [showAllFixed, setShowAllFixed] = React.useState(false)

  async function loadCategories() {
    setLoadingCats(true)
    try {
      const data = await listFinanceCostCategories()
      setCategories(data)
    } finally {
      setLoadingCats(false)
    }
  }

  async function loadFixedCosts() {
    setLoadingFixed(true)
    try {
      const data = await listFinanceFixedCosts({ monthStartDate, showAll: showAllFixed })
      setFixedCosts(data)
    } finally {
      setLoadingFixed(false)
    }
  }

  async function loadExpenses() {
    setLoadingExp(true)
    try {
      const data = await listFinanceExpenses({ monthStartDate })
      setExpenses(data)
    } finally {
      setLoadingExp(false)
    }
  }

  async function refreshAll() {
    await Promise.all([loadCategories(), loadFixedCosts(), loadExpenses()])
  }

  React.useEffect(() => {
    refreshAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthStartDate, refreshKey])

  React.useEffect(() => { loadFixedCosts() }, [showAllFixed])

  return (
    <div className="space-y-4">
      <Tabs
        tabs={[
          { value: 'recorrentes', label: 'Recorrentes' },
          { value: 'avulsos', label: 'Avulsos' },
          { value: 'categorias', label: 'Categorias' },
        ]}
        value={tab}
        onValueChange={(v) => setTab(v as any)}
      />

      {tab === 'recorrentes' && (
        <FinanceFixedCostsPanel
          categories={categories}
          items={fixedCosts}
          loading={loadingFixed || loadingCats}
          showAll={showAllFixed}
          onToggleShowAll={setShowAllFixed}
          onRefresh={refreshAll}
        />
      )}

      {tab === 'avulsos' && (
        <FinanceExpensesPanel
          monthStartDate={monthStartDate}
          categories={categories}
          items={expenses}
          loading={loadingExp || loadingCats}
          onRefresh={refreshAll}
        />
      )}

      {tab === 'categorias' && (
        <FinanceCostCategoriesPanel
          categories={categories}
          loading={loadingCats}
          onRefresh={refreshAll}
        />
      )}
    </div>
  )
}

